import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box, Typography, IconButton, Card, Button, Portal, Fade, Avatar,
    CircularProgress, Stack, Chip, Tooltip, Popper
} from '@mui/material';
import { Close, AutoAwesome, AddShoppingCart, Inventory2Outlined, StarRounded } from '@mui/icons-material';
import axios from 'axios';
import Apiurl from '../../Helpers/apiUrl';
import { formatCurrency } from '../../Helpers/currencyHelper';

/* ─────────────────────────────────────────────
   Tiny styled helper – keeps JSX clean
───────────────────────────────────────────── */
const Tag = ({ children, color = '#3b82f6' }) => (
    <Box component="span" sx={{
        display: 'inline-block',
        fontSize: '0.58rem',
        fontWeight: 700,
        letterSpacing: '0.3px',
        color,
        bgcolor: `${color}18`,
        border: `1px solid ${color}33`,
        px: 0.8,
        py: 0.25,
        borderRadius: '4px',
        lineHeight: 1,
    }}>
        {children}
    </Box>
);

/* ─────────────────────────────────────────────
   Individual scrollable product row card
───────────────────────────────────────────── */
const ProductRow = ({ item, onAdd, index }) => {
    const [hovered, setHovered] = useState(false);

    const stockColor =
        item.stockIndicator === 'high' ? '#22c55e' :
        item.stockIndicator === 'medium' ? '#f59e0b' : '#ef4444';

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1,
                borderRadius: '8px',
                bgcolor: hovered ? '#f8fafc' : 'transparent',
                border: '1px solid',
                borderColor: hovered ? '#e2e8f0' : 'transparent',
                transition: 'all 0.18s ease',
                cursor: 'default',
                animationDelay: `${index * 60}ms`,
            }}
        >
            {/* Rank badge */}
            <Box sx={{
                minWidth: 18, height: 18, borderRadius: '4px',
                bgcolor: index === 0 ? '#fef3c7' : '#f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: index === 0 ? '1px solid #fcd34d' : '1px solid #e2e8f0',
            }}>
                {index === 0
                    ? <StarRounded sx={{ fontSize: 11, color: '#f59e0b' }} />
                    : <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: '#94a3b8' }}>{index + 1}</Typography>
                }
            </Box>

            {/* Product image */}
            <Avatar
                variant="rounded"
                src={item.image || Apiurl.replace('/api/', '/img/no_image.jpeg')}
                slotProps={{
                    img: {
                        onError: (e) => {
                            const fallback = Apiurl.replace('/api/', '/img/no_image.jpeg');
                            if (e.target.src !== fallback) {
                                e.target.src = fallback;
                            }
                        }
                    }
                }}
                sx={{
                    width: 40, height: 40, borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#f8fafc',
                    flexShrink: 0,
                }}
            />


            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.2 }}>
                    <Typography sx={{
                        fontSize: '0.7rem', fontWeight: 800, color: '#0f172a',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: 100,
                    }}>
                        {item.sku_code}
                    </Typography>
                    <Tag color="#3b82f6">{item.brand}</Tag>
                </Stack>

                <Typography sx={{
                    fontSize: '0.6rem', color: '#64748b', lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {item.product_desc || '—'}
                </Typography>

                <Stack direction="row" spacing={0.5} sx={{ mt: 0.4 }} alignItems="center">
                    {item.collection && <Tag color="#8b5cf6">{item.collection}</Tag>}
                    <Box sx={{
                        width: 5, height: 5, borderRadius: '50%',
                        bgcolor: stockColor, flexShrink: 0,
                    }} />
                    <Typography sx={{ fontSize: '0.55rem', color: stockColor, fontWeight: 600 }}>
                        {item.stockIndicator === 'high' ? 'In Stock' :
                         item.stockIndicator === 'medium' ? 'Limited' : 'Low Stock'}
                    </Typography>
                </Stack>
            </Box>

            {/* Price + Add */}
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 900, color: '#0f172a',
                    fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.3px',
                }}>
                    {formatCurrency(item.mrp)}
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => onAdd(item)}
                    startIcon={<AddShoppingCart sx={{ fontSize: '10px !important' }} />}
                    sx={{
                        mt: 0.4,
                        bgcolor: '#0f172a',
                        color: '#fff',
                        borderRadius: '5px',
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.58rem',
                        px: 0.8,
                        py: 0.3,
                        minWidth: 0,
                        lineHeight: 1.4,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#1e293b',
                            boxShadow: '0 2px 8px rgba(15,23,42,0.25)',
                        },
                    }}
                >
                    Add
                </Button>
            </Box>
        </Box>
    );
};

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const SmartAutoRecommendations = ({
    activeSkuForSuggestion,
    setActiveSkuForSuggestion,
    selections,
    masterProduct,
    onAddProducts
}) => {
    const [showPopup, setShowPopup] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);
    const containerRef = useRef(null);

    const existingSkus = useMemo(() => {
        return new Set(selections.flatMap(s => s.rows).map(r => r.productCode).filter(Boolean));
    }, [selections]);

    useEffect(() => {
        if (activeSkuForSuggestion) {
            handleAutoTrigger(activeSkuForSuggestion);
            if (setActiveSkuForSuggestion) setActiveSkuForSuggestion(null);
        }
    }, [activeSkuForSuggestion]);

    const resetAutoHide = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setShowPopup(false), 18000);
    };

    const handleAutoTrigger = async (sku) => {
        setLoading(true);
        setShowPopup(true);
        const token = localStorage.getItem('u_token');
        try {
            const { data } = await axios.get(`${Apiurl}project/skuSuggetion/${sku}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const skuList = data?.data?.suggestedSkus ? data.data.suggestedSkus.split(',') : [];
            if (skuList.length > 0) {
                const enriched = skuList
                    .filter(s => !existingSkus.has(s))
                    .slice(0, 6)
                    .map(s => masterProduct.find(p => p.sku_code === s))
                    .filter(Boolean);

                setSuggestions(enriched.length > 0 ? enriched : []);
            } else {
                setSuggestions([]);
            }
            resetAutoHide();
        } catch (err) {
            console.error('AI Analysis Failed', err);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = (item) => {
        onAddProducts([{
            productCode: item.sku_code,
            brand: item.brand,
            description: item.product_desc,
            collection: item.collection,
            productType: item.product_typ_group,
            category: item.category,
            priceSegment: item.priceSegment,
            finish: item.finish,
            image: item.image,
            mrp: item.mrp,
            basePrice: item.basePrice || 0,
            gstRate: item.gst,
            stock: item.stockAvailable,
            stockIndicator: item.stockIndicator,
            quantity: 1,
            discountPercent: 0,
            discountPrice: 0,
        }]);
        // Remove from local list so it disappears immediately
        setSuggestions(prev => prev.filter(s => s.sku_code !== item.sku_code));
    };

    return (
        <Box 
            ref={containerRef}
            sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            zIndex: 1000,
        }}>

                {/* ── Main Panel ── */}
                <Popper
                    open={showPopup}
                    anchorEl={containerRef.current}
                    placement="top-end"
                    transition
                    sx={{ zIndex: 1400 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
                >
                    {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={220}>
                    <Card
                        onMouseEnter={() => { if (timerRef.current) clearTimeout(timerRef.current); }}
                        onMouseLeave={resetAutoHide}
                        elevation={0}
                        sx={{
                            width: 320,
                            borderRadius: '24px', // Updated to match rounded-3xl approx
                            background: 'rgba(255, 255, 255, 0.6)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255, 255, 255, 0.8)',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                            overflow: 'hidden',
                        }}
                    >
                        {/* ── Header ── */}
                        <Box sx={{
                            px: 1.75, py: 1.25,
                            bgcolor: '#0f172a',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box sx={{
                                    width: 22, height: 22, borderRadius: '6px',
                                    bgcolor: '#1e293b',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '1px solid #334155',
                                }}>
                                    <AutoAwesome sx={{ fontSize: 12, color: '#38bdf8' }} />
                                </Box>
                                <Box>
                                    <Typography sx={{
                                        fontSize: '0.7rem', fontWeight: 800,
                                        color: '#f8fafc', letterSpacing: '0.1px', lineHeight: 1,
                                    }}>
                                        Smart Suggestions
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.55rem', color: '#64748b', lineHeight: 1.2 }}>
                                        AI-powered recommendations
                                    </Typography>
                                </Box>
                            </Stack>
                            <IconButton
                                size="small"
                                onClick={() => setShowPopup(false)}
                                sx={{ p: 0.4, color: '#475569', '&:hover': { bgcolor: '#1e293b', color: '#f8fafc' } }}
                            >
                                <Close sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Box>

                        {/* ── Count strip ── */}
                        {!loading && suggestions.length > 0 && (
                            <Box sx={{
                                px: 1.75, py: 0.6,
                                bgcolor: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                                display: 'flex', alignItems: 'center', gap: 0.6,
                            }}>
                                <Inventory2Outlined sx={{ fontSize: 11, color: '#94a3b8' }} />
                                <Typography sx={{ fontSize: '0.6rem', color: '#64748b', fontWeight: 600 }}>
                                    {suggestions.length} product{suggestions.length > 1 ? 's' : ''} recommended for you
                                </Typography>
                            </Box>
                        )}

                        {/* ── Body ── */}
                        <Box sx={{ p: 1, maxHeight: 320, position: 'relative' }}>
                            {loading ? (
                                <Box sx={{
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                    py: 3, gap: 1.5,
                                }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <CircularProgress
                                            size={32} thickness={3}
                                            sx={{ color: '#e2e8f0' }}
                                            variant="determinate"
                                            value={100}
                                        />
                                        <CircularProgress
                                            size={32} thickness={3}
                                            sx={{ color: '#38bdf8', position: 'absolute', top: 0, left: 0 }}
                                        />
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#0f172a' }}>
                                            Analysing selection…
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                                            Finding best-fit products
                                        </Typography>
                                    </Box>
                                </Box>
                            ) : suggestions.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 2.5 }}>
                                    <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                                        No new suggestions available
                                    </Typography>
                                </Box>
                            ) : (
                                /* Scrollable product list */
                                <Box
                                    sx={{
                                        maxHeight: 300,
                                        overflowY: 'auto',
                                        pr: 0.25,
                                        /* Thin custom scrollbar */
                                        '&::-webkit-scrollbar': { width: 4 },
                                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                        '&::-webkit-scrollbar-thumb': {
                                            bgcolor: '#cbd5e1', borderRadius: 4,
                                        },
                                    }}
                                >
                                    <Stack spacing={0.25}>
                                        {suggestions.map((item, i) => (
                                            <ProductRow
                                                key={item.sku_code}
                                                item={item}
                                                index={i}
                                                onAdd={handleAdd}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>

                        {/* ── Footer ── */}
                        {!loading && suggestions.length > 0 && (
                            <Box sx={{
                                px: 1.75, py: 0.75,
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex', justifyContent: 'flex-end',
                            }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setShowPopup(false)}
                                    sx={{
                                        color: '#94a3b8', fontSize: '0.6rem',
                                        textTransform: 'none', fontWeight: 600,
                                        p: 0, minWidth: 0,
                                        '&:hover': { color: '#475569', bgcolor: 'transparent' },
                                    }}
                                >
                                    Dismiss
                                </Button>
                            </Box>
                        )}
                    </Card>
                </Fade>
                    )}
                </Popper>

                {/* ── FAB Trigger ── */}
                <Tooltip title="Smart Suggestions" placement="left" arrow>
                    <Box
                        onClick={() => {
                            if (!loading && suggestions.length > 0) {
                                setShowPopup(v => !v);
                            }
                        }}
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '10px',
                            bgcolor: '#0f172a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: loading || suggestions.length === 0 ? 'default' : 'pointer',
                            boxShadow: '0 4px 12px rgba(15,23,42,0.3)',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            '&:hover': {
                                transform: loading || suggestions.length === 0 ? 'none' : 'translateY(-2px)',
                                boxShadow: '0 8px 20px rgba(15,23,42,0.35)',
                            },
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={18} thickness={5} sx={{ color: '#38bdf8' }} />
                        ) : (
                            <AutoAwesome sx={{
                                fontSize: 18,
                                color: suggestions.length > 0 ? '#38bdf8' : '#475569',
                            }} />
                        )}

                        {/* Badge */}
                        {suggestions.length > 0 && !loading && !showPopup && (
                            <Box sx={{
                                position: 'absolute',
                                top: -4, right: -4,
                                minWidth: 16, height: 16,
                                bgcolor: '#ef4444',
                                borderRadius: '8px',
                                border: '2px solid #ffffff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                px: 0.4,
                            }}>
                                <Typography sx={{ fontSize: '0.5rem', fontWeight: 900, color: '#fff' }}>
                                    {suggestions.length}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Tooltip>
            </Box>
    );
};

export default SmartAutoRecommendations;